import React, { useState } from 'react';
import { Button, Snackbar, Alert, CircularProgress } from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { CustomWidthTooltipNoImage } from '../utils/customWidthToolTipUtilNoImage';

const CSVExportButton = ({ 
  table, 
  endpoint, 
  filename, 
  buttonText = 'Export CSV',
  params = {},
  sx = {}
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const exportToCSV = async () => {
    setIsExporting(true);
    
    try {
      let exportUrl;
      if (endpoint) {
        exportUrl = `${process.env.REACT_APP_API}${endpoint}`;
      } else if (table) {
        exportUrl = `${process.env.REACT_APP_API}/${table}/export`;
      } else {
        toast.error('Either table name or endpoint must be provided');
      }

      if (Object.keys(params).length > 0) {
        const queryParams = new URLSearchParams(params);
        exportUrl += `?${queryParams.toString()}`;
      }

      const response = await fetch(exportUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        toast.error(`Export failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      let downloadFilename;
      if (filename) {
        downloadFilename = filename;
      } else {
        const timestamp = new Date().toISOString().split('T')[0];
        const tableName = table || 'export';
        downloadFilename = `${tableName}-export-${timestamp}.csv`;
      }
      
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setNotification({
        open: true,
        message: `${table || 'Data'} Exported`,
        severity: 'success'
      });
      toast.success(`Export Succesful`);

    } catch (error) {
      console.error('CSV export error:', error);
      setNotification({
        open: true,
        message: `Export failed: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const toolTip = "Generate CSV reports easily imported into EXCEL or other CSV aware software.";

  return (
    <CustomWidthTooltipNoImage title={ toolTip } placement="top" dynamicWidth="250px" dyanmicHeight="25px" >
      <Button
        variant="contained"
        onClick={exportToCSV}
        disabled={isExporting}
        startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <FileDownload />}
        sx={{
          backgroundColor: '#2a2a2a',
          color: '#ffffff',
          height: '36px',
          textTransform: 'none',
          fontWeight: 500,
          letterSpacing: '0.02em',
          borderRadius: '6px',
          fontSize: '0.875rem',
          px: 2,
          minWidth: 'auto',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          border: '1px solid #555555',
          '&:hover': {
            backgroundColor: '#404040',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            transform: 'translateY(-1px)',
          },
          '&:disabled': {
            backgroundColor: '#1a1a1a',
            color: '#888888',
            boxShadow: 'none',
            transform: 'none',
            border: '1px solid #333333',
          },
          ...sx
        }}
      >
        {isExporting ? 'Exporting...' : buttonText}
      </Button>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ 
            width: '100%',
            backgroundColor: notification.severity === 'error' ? '#666666' : '#888888',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            },
            '& .MuiAlert-action': {
              color: 'white'
            }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </CustomWidthTooltipNoImage>
  );
};

export default CSVExportButton;